import { toast } from "sonner";
import storage from "../storage";
import router from "@/router";

const getRoutes = () => {
  const vid = storage.getItem("vid");

  return {
    signIn: "/sign-in",
    forgotPassword: "/forgot-password",
    dashboard: `/dashboard/${vid}`,
    vendor: `/vendor/${vid}/settings`,
    vendorUser: `/vendor/${vid}/profile`,
    invoices: `/vendor/${vid}/invoices`,
    products: `/vendor/${vid}/products`,
    inventory: `/vendor/${vid}/inventory`,
    inventoryDetails: (iid?: string) =>
      iid ? `/vendor/${vid}/inventory/${iid}` : `/vendor/${vid}/inventory`,
    product: (pid?: string) =>
      pid ? `/vendor/${vid}/product/${pid}` : `/vendor/${vid}/product`,
    brands: `/vendor/${vid}/brands`,
    brand: (bid?: string) =>
      bid ? `/vendor/${vid}/brands/${bid}` : `/vendor/${vid}/brands`,
    analytics: `/vendor/${vid}/analytics`,
    campaigns: `/vendor/${vid}/campaigns`,
    orders: `/vendor/${vid}/orders`,
  };
};

const getAuthenticatedPages = () => {
  const vid = storage.getItem("vid");
  const path = router.state.location.pathname;

  return {
    dashboard: new RegExp(`^/dashboard/${vid}$`).test(path),
    vendor: new RegExp(`^/vendor/${vid}/settings$`).test(path),
    vendorUser: new RegExp(`^/vendor/${vid}/profile$`).test(path),
    invoices: new RegExp(`^/vendor/${vid}/invoices$`).test(path),
    products: new RegExp(`^/vendor/${vid}/products$`).test(path),
    inventory: new RegExp(`^/vendor/${vid}/inventory$`).test(path),
    brands: new RegExp(`^/vendor/${vid}/brands$`).test(path),
    brand: new RegExp(`^/vendor/${vid}/brands/\\w+$`).test(path),
    analytics: new RegExp(`^/vendor/${vid}/analytics$`).test(path),
    campaigns: new RegExp(`^/vendor/${vid}/campaigns$`).test(path),
    product: new RegExp(`^/vendor/${vid}/product/\\w+$`).test(path),
    orders: new RegExp(`^/vendor/${vid}/orders$`).test(path),
  };
};

const handleErrorToast = (error: unknown) => {
  if (error instanceof Error) {
    toast.error(error.message);
  } else {
    toast.error("An unknown error occurred.");
  }
};

const formatPrice = (price: number, currency: string) => {
  const roundedPrice = Math.round(price * 100) / 100;
  const locale = currency.toUpperCase();

  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(roundedPrice);
  } catch {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(roundedPrice);
  }
};

const formatDate = (date: string) => {
  if (!date || typeof date !== "string" || date.trim() === "") {
    return "Invalid Date";
  }

  const parsedDate = new Date(date);

  if (isNaN(parsedDate.getTime()) || parsedDate.getTime() < 0) {
    return "Invalid Date";
  }

  const dateStr = date.trim();
  if (
    dateStr === "0000-00-00" ||
    dateStr === "0000-00-00 00:00:00" ||
    dateStr === "null" ||
    dateStr === "undefined"
  ) {
    return "Invalid Date";
  }

  return new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Tbilisi",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  })
    .format(parsedDate)
    .replace(/\//g, ".");
};

const formatProductTagValue = (value: string) => {
  const titleCase = (str: string) => {
    const abbreviations = ["PDO"];

    return str.replace(/\w\S*/g, (word) => {
      const upperWord = word.toUpperCase();
      if (abbreviations.includes(upperWord)) {
        return upperWord;
      }
      return word.charAt(0).toUpperCase() + word.substr(1).toLowerCase();
    });
  };

  return value
    .replace(/pdo:\s*/i, "PDO: ")
    .replace(/region:\s*/i, "Region: ")
    .replace(/grape:\s*/i, "Grape: ")
    .replace(
      /(PDO: |Region: |Grape: )(.+)/,
      (_, prefix, suffix) => prefix + titleCase(suffix)
    )
    .replace(/^(?!PDO:|Region:|Grape:)(.*)/, (_, match) => titleCase(match));
};

const getProductTagStyle = (tag: { id: string; value: string }) => {
  const lowerValue = tag.value.toLowerCase();

  if (lowerValue.includes("pdo")) {
    return "bg-amber-100 text-amber-800 border-amber-200";
  }

  if (lowerValue.includes("region")) {
    return "bg-emerald-100 text-emerald-800 border-emerald-200";
  }

  const hash = tag.id.split("").reduce((a, b) => {
    a = (a << 5) - a + b.charCodeAt(0);
    return a & a;
  }, 0);

  const colors = [
    "bg-blue-100 text-blue-800 border-blue-200",
    "bg-purple-100 text-purple-800 border-purple-200",
    "bg-pink-100 text-pink-800 border-pink-200",
    "bg-indigo-100 text-indigo-800 border-indigo-200",
  ];

  return colors[Math.abs(hash) % colors.length];
};

export {
  getRoutes,
  getAuthenticatedPages,
  handleErrorToast,
  formatDate,
  formatPrice,
  formatProductTagValue,
  getProductTagStyle,
};
